import { useMemo } from "react";

import {
  assignmentExists,
  assignmentIncludesExamQuestions,
  getAssignmentExamQuestionIds,
  makeGetAssignmentAggregatedQuestionsMarks
} from "seneca-common/features/assignments/features/student-assignments/state";
import { makeGetUnstudiedQuestionIds } from "seneca-common/features/assignments/features/student-assignments/state/selectors";
import useBatchFetchQuestions from "seneca-common/features/exam-questions/hooks/useBatchFetchQuestions";
import {
  useMemoSelectorOnCondition,
  useSelectorOnConditionWithArgs,
  useSelectorWithArgs
} from "seneca-common/utils/hooks/selectors";

import { makeLinkToAssignmentExamQuestionsSessionWithState } from "features/classroom/routes/classroom/routes/course/routes/course-exam-questions/links";

export function useGetExamQuestionsData(
  assignmentId: string,
  courseId?: string
) {
  const _assignmentExists = useSelectorWithArgs(assignmentExists, assignmentId);

  const includesExamQuestions = useSelectorOnConditionWithArgs(
    !!_assignmentExists,
    assignmentIncludesExamQuestions,
    assignmentId
  );

  const assignmentQuestionIds: string[] =
    (useSelectorOnConditionWithArgs(
      !!includesExamQuestions,
      getAssignmentExamQuestionIds,
      assignmentId
    )?.toJS() as string[]) || [];

  const numOfQuestionsAssigned = assignmentQuestionIds.length;

  const questionIdsToStudy: string[] =
    (useMemoSelectorOnCondition(
      includesExamQuestions,
      makeGetUnstudiedQuestionIds,
      assignmentId
    )?.toJS() as string[]) || [];

  const { data: assignmentExamQuestions } = useBatchFetchQuestions({
    questionIds: assignmentQuestionIds,
    assignmentId
  });

  const hasQuestionsToStudy = questionIdsToStudy?.length > 0;

  const linkToStartSession = hasQuestionsToStudy
    ? makeLinkToAssignmentExamQuestionsSessionWithState({
        courseId: courseId!,
        selectedQuestionIds: questionIdsToStudy as string[],
        assignmentId
      })
    : null;

  const questionIds = useSelectorOnConditionWithArgs(
    includesExamQuestions,
    getAssignmentExamQuestionIds,
    assignmentId
  );

  const maxPossibleTotalMarks =
    useMemo(
      () =>
        questionIds?.reduce((acc, id) => {
          const totalMarks = assignmentExamQuestions?.find(
            q => q.questionId === id
          )?.marksTotal;
          return acc + (totalMarks ?? 0);
        }, 0),
      [assignmentExamQuestions, questionIds]
    ) || 0;

  const marks = useMemoSelectorOnCondition(
    includesExamQuestions,
    makeGetAssignmentAggregatedQuestionsMarks,
    assignmentId
  );

  const marksReceived = marks?.marksReceived || 0;

  const calculateScore =
    includesExamQuestions &&
    maxPossibleTotalMarks > 0 &&
    questionIdsToStudy.length === 0;

  const score = calculateScore ? marksReceived / maxPossibleTotalMarks : 0;
  const scoreRatioDisplay = calculateScore
    ? `${marksReceived} / ${maxPossibleTotalMarks}`
    : `-- / ${maxPossibleTotalMarks}`;

  return {
    assignmentExamQuestions,
    linkToStartSession,
    numOfQuestionsAssigned,
    questionIdsToStudy,
    score,
    scoreRatioDisplay,
    hasQuestionsToStudy
  };
}
